import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decoratorAPI } from '../utils/api';
import DecoratorCard from '../components/Decorators/DecoratorCard';
import { SEO_CONFIGS } from "../utils/seoHelper";
import SEOHead from "../components/SEOHead";
import './DecoratorsPage.css';

/* ══════════════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════════════ */
const VENDOR_TYPES = [
  { value: '',               label: 'Sab Vendors',    icon: '🎪', plural: 'Vendors',         seoTitle: 'Best Event Vendors' },
  { value: 'decorator',      label: 'Decorators',     icon: '🎨', plural: 'Decorators',      seoTitle: 'Best Decorators' },
  { value: 'photographer',   label: 'Photographers',  icon: '📸', plural: 'Photographers',   seoTitle: 'Best Wedding Photographers' },
  { value: 'mehendi_artist', label: 'Mehendi',        icon: '🌸', plural: 'Mehendi Artists', seoTitle: 'Best Mehendi Artists' },
  { value: 'dj',             label: 'DJ',             icon: '🎵', plural: 'DJs',             seoTitle: 'Best DJs for Wedding' },
  { value: 'makeup_artist',  label: 'Makeup',         icon: '💄', plural: 'Makeup Artists',  seoTitle: 'Best Makeup Artists' },
  { value: 'cake_catering',  label: 'Cake & Food',    icon: '🎂', plural: 'Cake & Caterers', seoTitle: 'Best Cake & Catering' },
  { value: 'tent_furniture', label: 'Tent House',     icon: '⛺', plural: 'Tent Houses',     seoTitle: 'Best Tent Houses' },
  { value: 'videographer',   label: 'Videographer',   icon: '🎥', plural: 'Videographers',   seoTitle: 'Best Videographers' },
  { value: 'anchor',         label: 'Anchor',         icon: '🎤', plural: 'Anchors',         seoTitle: 'Best Event Anchors' },
  { value: 'wedding_car',    label: 'Wedding Car',    icon: '🚗', plural: 'Wedding Cars',    seoTitle: 'Best Wedding Cars' },
];

const METRO_CITIES = [
  { value: '',          label: 'All India',  icon: '🇮🇳', desc: 'Poore India mein' },
  { value: 'Mumbai',    label: 'Mumbai',     icon: '🌊',  desc: 'Maharashtra' },
  { value: 'Delhi',     label: 'Delhi',      icon: '🏛️',  desc: 'NCR Region' },
  { value: 'Bangalore', label: 'Bangalore',  icon: '🌿',  desc: 'Karnataka' },
  { value: 'Hyderabad', label: 'Hyderabad',  icon: '💎',  desc: 'Telangana' },
  { value: 'Chennai',   label: 'Chennai',    icon: '🌴',  desc: 'Tamil Nadu' },
  { value: 'Kolkata',   label: 'Kolkata',    icon: '🌺',  desc: 'West Bengal' },
  { value: 'Pune',      label: 'Pune',       icon: '🎓',  desc: 'Maharashtra' },
  { value: 'Ahmedabad', label: 'Ahmedabad',  icon: '🦋',  desc: 'Gujarat' },
];

const MORE_CITIES = [
  { value: 'Raipur',      label: 'Raipur',      icon: '⭐' },
  { value: 'Bhilai',      label: 'Bhilai',      icon: '🏗️' },
  { value: 'Durg',        label: 'Durg',        icon: '🏰' },
  { value: 'Bilaspur',    label: 'Bilaspur',    icon: '🌟' },
  { value: 'Korba',       label: 'Korba',       icon: '🔥' },
  { value: 'Rajnandgaon', label: 'Rajnandgaon', icon: '🌿' },
  { value: 'Jaipur',      label: 'Jaipur',      icon: '🕌' },
  { value: 'Lucknow',     label: 'Lucknow',     icon: '🌙' },
  { value: 'Bhopal',      label: 'Bhopal',      icon: '🏞️' },
  { value: 'Indore',      label: 'Indore',      icon: '🌆' },
  { value: 'Nagpur',      label: 'Nagpur',      icon: '🍊' },
  { value: 'Surat',       label: 'Surat',       icon: '💫' },
  { value: 'Chandigarh',  label: 'Chandigarh',  icon: '🌸' },
  { value: 'Gurgaon',     label: 'Gurgaon',     icon: '🏢' },
  { value: 'Noida',       label: 'Noida',       icon: '🌇' },
  { value: 'Vadodara',    label: 'Vadodara',    icon: '🎭' },
  { value: 'Agra',        label: 'Agra',        icon: '🕍' },
  { value: 'Varanasi',    label: 'Varanasi',    icon: '🪔' },
  { value: 'Nashik',      label: 'Nashik',      icon: '🍇' },
  { value: 'Ranchi',      label: 'Ranchi',      icon: '🌲' },
  { value: 'Guwahati',    label: 'Guwahati',    icon: '🦅' },
  { value: 'Dehradun',    label: 'Dehradun',    icon: '🏔️' },
  { value: 'Amritsar',    label: 'Amritsar',    icon: '🪯' },
  { value: 'Ludhiana',    label: 'Ludhiana',    icon: '🌾' },
  { value: 'Patna',       label: 'Patna',       icon: '🏯' },
  { value: 'Bhubaneswar', label: 'Bhubaneswar', icon: '🛕' },
  { value: 'Kochi',       label: 'Kochi',       icon: '⛵' },
  { value: 'Goa',         label: 'Goa',         icon: '🏖️' },
  { value: 'Mysore',      label: 'Mysore',      icon: '👑' },
  { value: 'Coimbatore',  label: 'Coimbatore',  icon: '🌺' },
];

const ALL_CITIES_LIST = [
  ...METRO_CITIES.filter(c => c.value).map(c => c.value),
  ...MORE_CITIES.map(c => c.value),
];

const SERVICES = [
  { value: '',              label: 'Sab Events',    icon: '🎊' },
  { value: 'Shaadi',        label: 'Shaadi',        icon: '💍' },
  { value: 'Engagement',    label: 'Engagement',    icon: '💒' },
  { value: 'Birthday',      label: 'Birthday',      icon: '🎂' },
  { value: 'Rice Ceremony', label: 'Rice Ceremony', icon: '🍚' },
  { value: 'Griha Pravesh', label: 'Griha Pravesh', icon: '🏠' },
  { value: 'Corporate',     label: 'Corporate',     icon: '💼' },
  { value: 'Baby Shower',   label: 'Baby Shower',   icon: '👶' },
  { value: 'Anniversary',   label: 'Anniversary',   icon: '🌹' },
];

const BUDGETS = [
  { label: 'Sab',       min: '',     max: '',      icon: '✨' },
  { label: '₹5k–15k',  min: 5000,   max: 15000,   icon: '💚' },
  { label: '₹15k–50k', min: 15000,  max: 50000,   icon: '💛' },
  { label: '₹50k–1L',  min: 50000,  max: 100000,  icon: '🧡' },
  { label: '₹1L+',     min: 100000, max: '',       icon: '💎' },
];

const RATINGS = [
  { label: 'Sab',  value: '' },
  { label: '4.5+', value: '4.5' },
  { label: '4.0+', value: '4.0' },
  { label: '3.5+', value: '3.5' },
];

const SORT_OPTIONS = [
  { value: '',           label: 'Default' },
  { value: 'rating',     label: '⭐ Rating se' },
  { value: 'price_asc',  label: '💰 Kam Price' },
  { value: 'price_desc', label: '💎 Zyada Price' },
  { value: 'popular',    label: '🔥 Popular' },
  { value: 'newest',     label: '🆕 Naye Pehle' },
];

/* ══════════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════════ */
const normalizeCity = (val) => {
  if (!val) return val;
  return val.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ══════════════════════════════════════════════════════════
   FLOATING PARTICLES BACKGROUND
══════════════════════════════════════════════════════════ */
function FloatingParticles() {
  return (
    <div className="dp-particles" aria-hidden="true">
      {Array.from({ length: 28 }).map((_, i) => (
        <span key={i} className={`dp-particle dp-particle--${(i % 5) + 1}`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${(Math.random() * 8).toFixed(2)}s`,
            animationDuration: `${(6 + Math.random() * 10).toFixed(2)}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════════════════ */
function SkeletonGrid({ view }) {
  return (
    <div className={`dp-grid dp-grid--${view}`}>
      {Array.from({ length: 9 }).map((_, i) => (
        <div className="dp-skel-card" key={i} style={{ animationDelay: `${i * 0.07}s` }}>
          <div className="dp-skel-img">
            <div className="dp-skel-shimmer" />
          </div>
          <div className="dp-skel-body">
            <div className="dp-skel-badge" />
            <div className="dp-skel-line dp-skel-line--wide" />
            <div className="dp-skel-line dp-skel-line--mid" />
            <div className="dp-skel-line dp-skel-line--narrow" />
            <div className="dp-skel-row">
              <div className="dp-skel-dot" />
              <div className="dp-skel-dot" />
              <div className="dp-skel-dot" />
            </div>
            <div className="dp-skel-footer">
              <div className="dp-skel-price" />
              <div className="dp-skel-btn" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGINATION
══════════════════════════════════════════════════════════ */
function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  const getRange = () => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const arr = [1];
    if (page > 3) arr.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) arr.push(i);
    if (page < pages - 2) arr.push('…');
    arr.push(pages);
    return arr;
  };
  return (
    <div className="dp-pagination">
      <button className="dp-pg dp-pg--arr" onClick={() => onChange(page - 1)} disabled={page === 1}>
        <span>‹</span>
      </button>
      {getRange().map((p, i) =>
        p === '…'
          ? <span className="dp-pg-dots" key={`d${i}`}>…</span>
          : <button key={p} className={`dp-pg ${page === p ? 'dp-pg--active' : ''}`} onClick={() => onChange(p)}>
              <span>{p}</span>
            </button>
      )}
      <button className="dp-pg dp-pg--arr" onClick={() => onChange(page + 1)} disabled={page === pages}>
        <span>›</span>
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ACTIVE FILTER TAGS
══════════════════════════════════════════════════════════ */
function ActiveTags({ filters, onRemove, onClear }) {
  const allCities        = [...METRO_CITIES, ...MORE_CITIES];
  const activeCity       = allCities.find(c => c.value === filters.city);
  const activeService    = SERVICES.find(s => s.value === filters.service);
  const activeBudget     = BUDGETS.find(b => String(b.min) === String(filters.minPrice) && b.min !== '');
  const activeVendorType = VENDOR_TYPES.find(v => v.value === filters.vendorType);
  const tags = [
    filters.vendorType && { key: 'vendorType', label: `${activeVendorType?.icon} ${activeVendorType?.label}` },
    filters.city       && { key: 'city',       label: `${activeCity?.icon || '📍'} ${activeCity?.label || filters.city}` },
    filters.service    && { key: 'service',    label: `${activeService?.icon} ${activeService?.label}` },
    filters.minPrice   && { key: 'budget',     label: `${activeBudget?.icon || '💰'} ${activeBudget?.label || 'Budget'}` },
    filters.minRating  && { key: 'minRating',  label: `⭐ ${filters.minRating}+` },
    filters.verified   && { key: 'verified',   label: '✅ Verified Only' },
  ].filter(Boolean);
  if (!tags.length) return null;
  return (
    <div className="dp-active-bar">
      <span className="dp-active-lbl">
        <span className="dp-active-lbl__icon">◈</span> Active Filters
      </span>
      <div className="dp-active-tags">
        {tags.map(t => (
          <span className="dp-tag" key={t.key}>
            <span className="dp-tag__text">{t.label}</span>
            <button className="dp-tag__remove" onClick={() => onRemove(t.key)} aria-label="Remove filter">
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </span>
        ))}
      </div>
      <button className="dp-clear-all" onClick={onClear}>
        <span>Sab Hatao</span>
        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   METRO CITY CARDS
══════════════════════════════════════════════════════════ */
function MetroCityCards({ selectedCity, onSelect }) {
  return (
    <div className="dp-city-selector">
      <div className="dp-city-section-label">
        <span className="dp-section-label__line" />
        <span className="dp-section-label__text">🔥 Metro Cities</span>
        <span className="dp-section-label__line" />
      </div>
      <div className="dp-metro-grid">
        {METRO_CITIES.map(c => {
          const active = selectedCity === c.value;
          return (
            <button
              key={c.value}
              className={`dp-city-card ${active ? 'dp-city-card--active' : ''}`}
              onClick={() => onSelect(c.value)}
            >
              <span className="dp-city-card__glow" />
              <span className="dp-city-card__icon">{c.icon}</span>
              <span className="dp-city-card__name">{c.label}</span>
              {c.desc && <span className="dp-city-card__desc">{c.desc}</span>}
              {active && <span className="dp-city-card__dot" />}
            </button>
          );
        })}
      </div>

      <div className="dp-city-section-label dp-city-section-label--sm">
        <span className="dp-section-label__line" />
        <span className="dp-section-label__text">✨ More Cities</span>
        <span className="dp-section-label__line" />
      </div>
      <div className="dp-more-cities">
        {MORE_CITIES.map(c => {
          const active = selectedCity === c.value;
          return (
            <button
              key={c.value}
              className={`dp-city-pill ${active ? 'dp-city-pill--active' : ''}`}
              onClick={() => onSelect(c.value)}
            >
              <span className="dp-city-pill__icon">{c.icon}</span>
              <span>{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   STATS BAR
══════════════════════════════════════════════════════════ */
function StatsBar({ total, vendorPlural }) {
  const stats = [
    { num: total > 0 ? `${total}+` : '500+', lbl: vendorPlural,  icon: '🎯' },
    { num: '50+',  lbl: 'Cities',      icon: '🗺️' },
    { num: '10',   lbl: 'Vendor Types',icon: '🎪' },
    { num: '4.8★', lbl: 'Avg Rating',  icon: '⭐' },
    { num: '100%', lbl: 'Verified',    icon: '✅' },
  ];
  return (
    <div className="dp-stats-bar">
      <div className="dp-stats-inner">
        {stats.map((s, i) => (
          <div className="dp-stat-item" key={s.lbl}>
            <div className="dp-stat-icon">{s.icon}</div>
            <span className="dp-stat-num">{s.num}</span>
            <span className="dp-stat-lbl">{s.lbl}</span>
            {i < stats.length - 1 && <div className="dp-stat-divider" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   FILTER SECTION
══════════════════════════════════════════════════════════ */
function FilterPanel({ filters, filtersOpen, onApplyFilter, onApplyBudget, onRemoveFilter, onClearAll }) {
  const isCustomCity = filters.city && ![...METRO_CITIES, ...MORE_CITIES].find(c => c.value === filters.city);

  const chip = (active) => active ? 'dp-chip dp-chip--active' : 'dp-chip';

  return (
    <div className={`dp-filter-panel ${filtersOpen ? 'dp-filter-panel--open' : ''}`}>
      <div className="dp-filter-inner">

        {/* Vendor Type */}
        <div className="dp-filter-section">
          <div className="dp-filter-section__header">
            <span className="dp-filter-section__icon">🎪</span>
            <span>Vendor Type</span>
          </div>
          <div className="dp-chips">
            {VENDOR_TYPES.map(v => (
              <button key={v.value} className={chip(filters.vendorType === v.value)}
                onClick={() => onApplyFilter('vendorType', v.value)}>
                <span className="dp-chip__icon">{v.icon}</span>
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dp-filter-divider" />

        {/* City */}
        <div className="dp-filter-section">
          <div className="dp-filter-section__header">
            <span className="dp-filter-section__icon">📍</span>
            <span>Sheher — All India</span>
          </div>
          <MetroCityCards selectedCity={filters.city} onSelect={(val) => onApplyFilter('city', val)} />
          <div className={`dp-city-input-wrap ${isCustomCity ? 'dp-city-input-wrap--active' : ''}`}>
            <span className="dp-city-input__ico">🗺️</span>
            <input
              list="dp-cities-filter"
              placeholder="Koi aur sheher type karo..."
              value={isCustomCity ? filters.city : ''}
              onChange={e => onApplyFilter('city', e.target.value)}
              autoComplete="off"
              className="dp-city-input"
            />
            {isCustomCity && (
              <button className="dp-city-input__clear" onClick={() => onApplyFilter('city', '')}>
                <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            )}
            <datalist id="dp-cities-filter">
              {ALL_CITIES_LIST.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
        </div>

        <div className="dp-filter-divider" />

        {/* Event Type */}
        <div className="dp-filter-section">
          <div className="dp-filter-section__header">
            <span className="dp-filter-section__icon">🎊</span>
            <span>Event Type</span>
          </div>
          <div className="dp-chips">
            {SERVICES.map(s => (
              <button key={s.value} className={chip(filters.service === s.value)}
                onClick={() => onApplyFilter('service', s.value)}>
                <span className="dp-chip__icon">{s.icon}</span>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dp-filter-divider" />

        {/* Budget */}
        <div className="dp-filter-section">
          <div className="dp-filter-section__header">
            <span className="dp-filter-section__icon">💰</span>
            <span>Budget Range</span>
          </div>
          <div className="dp-chips dp-chips--budget">
            {BUDGETS.map(b => (
              <button
                key={b.label}
                className={chip(String(filters.minPrice) === String(b.min) && String(filters.maxPrice) === String(b.max))}
                onClick={() => onApplyBudget(b)}
              >
                <span className="dp-chip__icon">{b.icon}</span>
                <span>{b.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dp-filter-divider" />

        {/* Rating + Verified */}
        <div className="dp-filter-section dp-filter-section--row">
          <div className="dp-filter-subsection">
            <div className="dp-filter-section__header">
              <span className="dp-filter-section__icon">⭐</span>
              <span>Min Rating</span>
            </div>
            <div className="dp-chips">
              {RATINGS.map(r => (
                <button key={r.label} className={chip(filters.minRating === r.value)}
                  onClick={() => onApplyFilter('minRating', r.value)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <button
            className={`dp-verified-toggle ${filters.verified ? 'dp-verified-toggle--on' : ''}`}
            onClick={() => onApplyFilter('verified', !filters.verified)}
          >
            <span className="dp-verified-toggle__label">
              <span className="dp-verified-toggle__ico">✅</span>
              <span>Verified Only</span>
            </span>
            <div className="dp-toggle-track">
              <div className="dp-toggle-thumb" />
            </div>
          </button>
        </div>

        <ActiveTags filters={filters} onRemove={onRemoveFilter} onClear={onClearAll} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
export default function DecoratorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [decorators, setDecorators] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [view,       setView]       = useState('v3');
  const [sort,       setSort]       = useState('');
  const [search,     setSearch]     = useState('');
  const [scrollBtn,  setScrollBtn]  = useState(false);
  const [filtersOpen,setFiltersOpen]= useState(false);
  const [heroVisible,setHeroVisible]= useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const [filters, setFilters] = useState({
    vendorType: searchParams.get('vendorType') || '',
    city:       normalizeCity(searchParams.get('city') || ''),
    service:    searchParams.get('service')    || '',
    minPrice:   searchParams.get('minPrice')   || '',
    maxPrice:   searchParams.get('maxPrice')   || '',
    minRating:  searchParams.get('minRating')  || '',
    verified:   searchParams.get('verified') === 'true',
  });

  const resultsRef      = useRef(null);
  const debouncedSearch = useDebounce(search, 500);

  // Refs to always hold latest values — fixes stale closure in doFetch
  const filtersRef    = useRef(filters);
  const sortRef       = useRef(sort);
  const searchRef     = useRef(debouncedSearch);
  const mountedRef    = useRef(false); // prevents double-fetch on mount

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortRef.current = sort; }, [sort]);
  useEffect(() => { searchRef.current = debouncedSearch; }, [debouncedSearch]);

  const allCities       = [...METRO_CITIES, ...MORE_CITIES];
  const activeVendorType= VENDOR_TYPES.find(v => v.value === filters.vendorType) || VENDOR_TYPES[0];
  const activeCity      = allCities.find(c => c.value === filters.city);
  const isCustomCity    = filters.city && !activeCity;
  const cityLabel       = activeCity ? activeCity.label : filters.city;
  const hasFilters      = !!(filters.vendorType || filters.city || filters.service || filters.minPrice || filters.minRating || filters.verified);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // doFetch — no deps on state, reads everything from args only
  const doFetch = useCallback(async (f, p, s, q) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: p, limit: 12 };
      if (f.vendorType) params.vendorType = f.vendorType;
      if (f.city)       params.city       = f.city;
      if (f.service)    params.service    = f.service;
      if (f.minPrice)   params.minPrice   = f.minPrice;
      if (f.maxPrice)   params.maxPrice   = f.maxPrice;
      if (f.minRating)  params.minRating  = f.minRating;
      if (f.verified)   params.verified   = true;
      if (s)            params.sort       = s;
      if (q?.trim())    params.search     = q.trim();
      const { data } = await decoratorAPI.getAll(params);
      setDecorators(data.decorators || []);
      setTotal(data.total   || 0);
      setPages(data.pages   || 1);
      setPage(p);
    } catch {
      // Read plural from current filters ref to avoid stale closure
      const vt = VENDOR_TYPES.find(v => v.value === f.vendorType) || VENDOR_TYPES[0];
      setError(`${vt.plural} load nahi hue — dobara try karo!`);
      setDecorators([]);
    } finally {
      setLoading(false);
    }
  }, []); // no deps — all state passed as arguments

  // Initial fetch — runs once on mount
  useEffect(() => {
    doFetch(filtersRef.current, 1, sortRef.current, '');
  }, []); // eslint-disable-line

  // Debounced search fetch — skips on initial mount
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    doFetch(filtersRef.current, 1, sortRef.current, debouncedSearch);
  }, [debouncedSearch]); // eslint-disable-line

  useEffect(() => {
    const fn = () => setScrollBtn(window.scrollY > 450);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const syncURL = (f) => {
    const p = {};
    if (f.vendorType) p.vendorType = f.vendorType;
    if (f.city)       p.city       = f.city;
    if (f.service)    p.service    = f.service;
    if (f.minPrice)   p.minPrice   = String(f.minPrice);
    if (f.maxPrice)   p.maxPrice   = String(f.maxPrice);
    if (f.minRating)  p.minRating  = f.minRating;
    if (f.verified)   p.verified   = 'true';
    setSearchParams(p, { replace: true });
  };

  const applyFilter = (key, value) => {
    const val = key === 'city' ? normalizeCity(value) : value;
    const nf  = { ...filtersRef.current, [key]: val };
    filtersRef.current = nf;
    setFilters(nf);
    syncURL(nf);
    doFetch(nf, 1, sortRef.current, searchRef.current);
  };

  const applyBudget = (b) => {
    const nf = { ...filtersRef.current, minPrice: b.min, maxPrice: b.max };
    filtersRef.current = nf;
    setFilters(nf);
    syncURL(nf);
    doFetch(nf, 1, sortRef.current, searchRef.current);
  };

  const removeFilter = (key) => {
    let nf;
    if (key === 'budget')        nf = { ...filtersRef.current, minPrice: '', maxPrice: '' };
    else if (key === 'verified') nf = { ...filtersRef.current, verified: false };
    else                         nf = { ...filtersRef.current, [key]: '' };
    filtersRef.current = nf;
    setFilters(nf);
    syncURL(nf);
    doFetch(nf, 1, sortRef.current, searchRef.current);
  };

  const clearAll = () => {
    const clean = { vendorType: '', city: '', service: '', minPrice: '', maxPrice: '', minRating: '', verified: false };
    filtersRef.current = clean;
    setFilters(clean);
    setSearchParams({}, { replace: true });
    doFetch(clean, 1, sortRef.current, searchRef.current);
  };

  const handleSort = (s) => {
    sortRef.current = s;
    setSort(s);
    doFetch(filtersRef.current, 1, s, searchRef.current);
  };

  const handlePage = (p) => {
    doFetch(filtersRef.current, p, sortRef.current, searchRef.current);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const eyebrow  = filters.city ? `${activeCity?.icon || '📍'} ${cityLabel} — ${activeVendorType.plural}` : filters.vendorType ? `${activeVendorType.icon} ${activeVendorType.plural} · All India` : '🇮🇳 India ka #1 Event Services Platform';
  const heroTitle= activeVendorType.seoTitle || `Best ${activeVendorType.plural}`;
  const heroSub  = filters.vendorType ? `Shaadi se Birthday tak — har event ke liye perfect ${activeVendorType.label} sirf yahan milega` : 'Decorator, Photographer, Mehendi, DJ, Makeup — sab ek jagah. Poore India mein.';

  return (
    <div className="dp-page">
      <SEOHead {...SEO_CONFIGS.decorators} />

      {/* Background Layers */}
      <div className="dp-bg-layer" aria-hidden="true">
        <div className="dp-bg-mesh" />
        <div className="dp-bg-grid" />
        <div className="dp-bg-vignette" />
        <div className="dp-mandala dp-mandala--1" />
        <div className="dp-mandala dp-mandala--2" />
        <div className="dp-mandala dp-mandala--3" />
        <FloatingParticles />
      </div>

      <div className="dp-container">

        {/* ══ HERO ══════════════════════════════════════ */}
        <section className={`dp-hero ${heroVisible ? 'dp-hero--visible' : ''}`}>
          <div className="dp-hero__eyebrow">
            <span className="dp-eyebrow__pulse" />
            <span className="dp-eyebrow__text">{eyebrow}</span>
          </div>

          <h1 className="dp-hero__title">
            <span className="dp-hero__title-line dp-hero__title-line--1">
              <em>{heroTitle}</em>
            </span>
            <span className="dp-hero__title-line dp-hero__title-line--2">
              {filters.city ? `in ${cityLabel}` : 'Dhundho Apne Sheher Mein'}
            </span>
          </h1>

          <p className="dp-hero__sub">{heroSub}</p>

          {/* Luxury City Selector */}
          <div className="dp-hero__city-box">
            <div className="dp-hero__city-box-glow" />
            <MetroCityCards selectedCity={filters.city} onSelect={(val) => applyFilter('city', val)} />
            <div className={`dp-city-input-wrap dp-city-input-wrap--hero ${isCustomCity ? 'dp-city-input-wrap--active' : ''}`}>
              <span className="dp-city-input__ico">🗺️</span>
              <input
                list="dp-cities-hero"
                placeholder="Koi aur sheher? — Agra, Nashik, Goa, Amritsar..."
                value={isCustomCity ? filters.city : ''}
                onChange={e => applyFilter('city', e.target.value)}
                autoComplete="off"
                className="dp-city-input"
              />
              {isCustomCity && (
                <button className="dp-city-input__clear" onClick={() => applyFilter('city', '')}>
                  <svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                </button>
              )}
              <datalist id="dp-cities-hero">
                {ALL_CITIES_LIST.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Hero Ornament */}
          <div className="dp-ornament">
            <div className="dp-ornament__line" />
            <div className="dp-ornament__jewel">
              <span className="dp-ornament__diamond">◆</span>
              <span className="dp-ornament__dot">·</span>
              <span className="dp-ornament__diamond dp-ornament__diamond--sm">◆</span>
              <span className="dp-ornament__dot">·</span>
              <span className="dp-ornament__diamond">◆</span>
            </div>
            <div className="dp-ornament__line" />
          </div>
        </section>

        {/* ══ STATS ═════════════════════════════════════ */}
        <StatsBar total={total} vendorPlural={activeVendorType.plural} />

        {/* ══ SEARCH ════════════════════════════════════ */}
        <div className="dp-search-outer">
          <div className={`dp-search-wrap ${searchFocused ? 'dp-search-wrap--focused' : ''}`}>
            <span className="dp-search-wrap__glow" />
            <span className="dp-search-ico">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </span>
            <input
              className="dp-search"
              type="text"
              placeholder={`${activeVendorType.label} ka naam ya sheher search karo...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            {search && (
              <button className="dp-search-clear" onClick={() => setSearch('')}>
                <svg width="14" height="14" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </button>
            )}
            {loading && search && <span className="dp-search-spinner" />}
          </div>
        </div>

        {/* ══ FILTER PANEL ══════════════════════════════ */}
        <FilterPanel
          filters={filters}
          filtersOpen={filtersOpen}
          onApplyFilter={applyFilter}
          onApplyBudget={applyBudget}
          onRemoveFilter={removeFilter}
          onClearAll={clearAll}
        />

        {/* ══ RESULTS HEADER ════════════════════════════ */}
        <div className="dp-results-hdr" ref={resultsRef}>
          <div className="dp-results-count">
            {loading
              ? <span className="dp-count-loading">
                  <span className="dp-count-loading__dot" />
                  <span className="dp-count-loading__dot" />
                  <span className="dp-count-loading__dot" />
                </span>
              : (
                <span className="dp-count-text">
                  <strong className="dp-count-num">{total}</strong>
                  <span> {activeVendorType.plural} mile</span>
                  {filters.city && <em className="dp-count-em"> · {cityLabel}</em>}
                  {filters.vendorType && <em className="dp-count-em"> · {activeVendorType.label}</em>}
                  {search && <em className="dp-count-em"> · "{search}"</em>}
                </span>
              )
            }
          </div>
          <div className="dp-controls">
            <button
              className={`dp-filter-toggle-btn ${hasFilters ? 'dp-filter-toggle-btn--active' : ''} ${filtersOpen ? 'dp-filter-toggle-btn--open' : ''}`}
              onClick={() => setFiltersOpen(o => !o)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>Filters</span>
              {hasFilters && <span className="dp-filter-badge" />}
            </button>

            <div className="dp-sort-wrap">
              <select className="dp-sort" value={sort} onChange={e => handleSort(e.target.value)}>
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span className="dp-sort__arrow">
                <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>

            <div className="dp-view-toggle">
              {[
                { id: 'v3', icon: '⊞' },
                { id: 'v2', icon: '▦' },
                { id: 'v1', icon: '☰' },
              ].map(v => (
                <button
                  key={v.id}
                  className={`dp-vbtn ${view === v.id ? 'dp-vbtn--active' : ''}`}
                  onClick={() => setView(v.id)}
                  title={`${v.id} view`}
                >
                  {v.icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ══ GRID ══════════════════════════════════════ */}
        {loading ? (
          <SkeletonGrid view={view} />
        ) : error ? (
          <div className="dp-empty dp-empty--error">
            <div className="dp-empty__icon-wrap">
              <span className="dp-empty__icon">⚠️</span>
              <div className="dp-empty__icon-ring" />
            </div>
            <h3 className="dp-empty__title">{error}</h3>
            <button className="dp-empty__btn" onClick={() => doFetch(filters, 1, sort, debouncedSearch)}>
              <span>🔄 Dobara Try Karo</span>
            </button>
          </div>
        ) : decorators.length === 0 ? (
          <div className="dp-empty">
            <div className="dp-empty__icon-wrap">
              <span className="dp-empty__icon">😕</span>
              <div className="dp-empty__icon-ring" />
            </div>
            <h3 className="dp-empty__title">Koi {activeVendorType.label} nahi mila yaar!</h3>
            <p className="dp-empty__sub">Thodi filters change karo, zaroor kuch milega</p>
            {hasFilters && (
              <button className="dp-empty__btn" onClick={clearAll}>
                <span>✨ Sab Filters Hatao</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={`dp-grid dp-grid--${view}`}>
              {decorators.map((d, i) => (
                <div
                  key={d._id}
                  className="dp-card-wrapper"
                  style={{ '--delay': `${i * 0.04}s` }}
                >
                  <DecoratorCard decorator={d} />
                </div>
              ))}
            </div>
            <Pagination page={page} pages={pages} onChange={handlePage} />
          </>
        )}

      </div>

      {/* ══ FOOTER ════════════════════════════════════ */}
      <footer className="dp-footer">
        <div className="dp-footer__bg" />
        <div className="dp-footer__inner">
          <div className="dp-footer__ornament">
            <div className="dp-footer__orn-line" />
            <span className="dp-footer__orn-gem">◈</span>
            <div className="dp-footer__orn-line" />
          </div>
          <div className="dp-footer__brand">
            <span className="dp-footer__brand-icon">✦</span>
            DecorHub
            <span className="dp-footer__brand-icon">✦</span>
          </div>
          <p className="dp-footer__tagline">
            {filters.city
              ? `${cityLabel} ka best ${activeVendorType.label} aaj hi book karo 💛`
              : 'Poore India mein apne event ke liye best vendor book karo 💛'}
          </p>
          <div className="dp-footer__cities">
            Mumbai · Delhi · Bangalore · Hyderabad · Chennai · Kolkata · Pune · Ahmedabad · Raipur · Jaipur · Lucknow · Surat · Goa · Amritsar
          </div>
          <div className="dp-footer__copy">© 2025 DecorHub · Made with ❤️ for India</div>
        </div>
      </footer>

      {/* Scroll to top */}
      <button
        className={`dp-scroll-top ${scrollBtn ? 'dp-scroll-top--show' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M9 14V4M4 9l5-5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}